import { useTranslation } from "react-i18next";
import { ExclamationCircleIcon } from "@heroicons/react/solid";
import { AxiosError } from "axios";

interface ErrorBannerProps {
  message: string | Error;
  type?: "error" | "warning" | "info" | "success";
}

const ErrorBanner = ({ message, type = "error" }: ErrorBannerProps) => {
  const { t } = useTranslation();

  const styles = {
    error: {
      bgColor: "bg-red-50",
      textColor: "text-red-800",
      iconColor: "text-red-400",
    },
    warning: {
      bgColor: "bg-yellow-50",
      textColor: "text-yellow-800",
      iconColor: "text-yellow-400",
    },
    info: {
      bgColor: "bg-blue-50",
      textColor: "text-blue-800",
      iconColor: "text-blue-400",
    },
    success: {
      bgColor: "bg-green-50",
      textColor: "text-green-800",
      iconColor: "text-green-400",
    },
  };

  const { bgColor, textColor, iconColor } = styles[type];

  const getErrorMessage = (error: string | Error) => {
    if (error instanceof Error) {
      const axiosError = error as AxiosError;
      if (axiosError.response?.data?.message) {
        return axiosError.response.data.message;
      }
      if (axiosError.config) {
        return `Request failed: ${error.message}`;
      }
      return error.message;
    }
    return error;
  };

  return (
    <div className={`rounded-md ${bgColor} p-4 my-4`}>
      <div className="flex">
        <div className="flex-shrink-0">
          <ExclamationCircleIcon
            className={`h-5 w-5 ${iconColor}`}
            aria-hidden="true"
          />
        </div>
        <div className="ml-3">
          <h3 className={`text-sm font-medium ${textColor}`}>
            {type === "error"
              ? t("channel:error-occurred", "An error occurred")
              : t("channel:warning", "Warning")}
          </h3>
          <div
            className={`mt-2 text-sm ${
              type === "error" ? "text-red-700" : "text-yellow-700"
            }`}
          >
            <p>{getErrorMessage(message)}</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ErrorBanner;
